<template>
  <div class="h-100 d-flex flex-column">
    <a-card
      v-for="(process, processIndex) in taskProcesses"
      :key="process.name"
      class="process-container"
      hoverable
      :title="process.name"
      :bordered="false"
      :body-style="{
        flexGrow: '1',
        height: '1px',
        overflowY: 'auto',
        overflowX: 'hidden',
      }"
    >
      <div style="text-align: right">
        <a-space>
          <a-button
            v-if="advanceFeatures['Draw Object']"
            style="background-color: #578fca; border-color: #578fca"
            type="primary"
            size="small"
            :loading="loadingCustomCode"
            @click="openPopup('draw')"
          >
            Draw Object
          </a-button>
          <a-button
            v-if="advanceFeatures['Reset']"
            style="background-color: #df6d14; border-color: #df6d14"
            type="primary"
            size="small"
            :loading="loadingCustomCode"
            @click="openPopup('reset')"
          >
            Reset
          </a-button>
          <a-button
            style="background-color: #f10000; border-color: #f10000"
            type="primary"
            size="small"
            :loading="loadingCustomCode"
            @click="openPopup('__init__')"
          >
            Code
          </a-button>
        </a-space>
      </div>

      <div v-for="(step, stepIndex) in steps(processIndex)" :key="step.name">
        <h6 class="mb-3">
          {{ step.name }}
        </h6>

        <template
          v-for="(substep, substepIndex) in substeps(processIndex, stepIndex)"
          :key="substep"
        >
          <!-- Bool Express -->
          <!-- Selection bar -->
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
            "
          >
            <a-space>
              <div style="display: flex; align-items: center; width: 300px">
                <a-tooltip placement="top">
                  <template #title>{{ substep }}</template>
                  <h6
                    style="
                      font-weight: bold;
                      margin: 0;
                      max-width: 220px;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                  >
                    {{
                      substep.length > 60
                        ? substep.substring(0, 57) + '...'
                        : substep
                    }}
                  </h6>
                </a-tooltip>
              </div>

              <a-checkbox
                v-model:checked="verifiedSteps[stepIndex][substepIndex]"
                >verify</a-checkbox
              >

              <a-tooltip placement="top">
                <template #title>Verified Frames</template>
                <a-input-number
                  v-if="verifiedSteps[stepIndex][substepIndex]"
                  v-model:value="frameCounts[stepIndex][substepIndex]"
                  placeholder="verified frames"
                  style="width: 60px"
                  :min="1"
                  :step="1"
                  @keydown="handleKeyDownNumber"
                  @blur="sanitizeNumberInput(stepIndex, substepIndex, 'frames')"
                />
              </a-tooltip>

              <a-tooltip v-if="advanceFeatures['Patience']" placement="top">
                <template #title>Patience</template>
                <a-input-number
                  v-model:value="patienceValue[stepIndex][substepIndex]"
                  placeholder="Patience"
                  style="width: 60px"
                  :min="0"
                  :step="1"
                  @keydown="handleKeyDownNumber"
                  @blur="
                    sanitizeNumberInput(stepIndex, substepIndex, 'patience')
                  "
                />
              </a-tooltip>

              <a-tooltip v-if="advanceFeatures['IOU']" placement="top">
                <template #title>IOU Threshold</template>
                <a-input-number
                  v-model:value="iouVlaue[stepIndex][substepIndex]"
                  placeholder="IOU Threshold"
                  style="width: 60px"
                  :min="0.1"
                  :max="1.0"
                  :step="0.1"
                  @keydown="handleKeyDownThreshold"
                  @blur="sanitizeNumberInput(stepIndex, substepIndex, 'iou')"
                />
              </a-tooltip>
            </a-space>

            <a-space>
              <a-button
                style="background-color: #9accf2; border-color: #9accf2"
                type="primary"
                size="small"
                @click="
                  addExpression('object', process.name, step.name, substep)
                "
              >
                Object
              </a-button>

              <a-button
                style="background-color: #5dc877; border-color: #5dc877"
                type="primary"
                size="small"
                @click="
                  addExpression('region', process.name, step.name, substep)
                "
              >
                Region
              </a-button>

              <a-button
                style="background-color: #f1ae00; border-color: #f1ae00"
                type="primary"
                size="small"
                @click="
                  addExpression('operator', process.name, step.name, substep)
                "
              >
                Operator
              </a-button>

              <a-button
                style="background-color: #ce15e7; border-color: #ce15e7"
                type="primary"
                size="small"
                @click="addExpression('time', process.name, step.name, substep)"
              >
                Time
              </a-button>

              <div>
                <a-button
                  style="background-color: #f10000; border-color: #f10000"
                  type="primary"
                  size="small"
                  :loading="loadingCustomCode"
                  @click="openPopup(substep)"
                >
                  Code
                </a-button>
              </div>
            </a-space>
          </div>

          <!-- Expression -->

          <div class="py-2" style="width: 85vw">
            <template
              v-for="(exp, index) in expressions[process.name][step.name][
                substep
              ]"
              :key="exp.type + index"
              style="display: flex; flex-direction: row; width: 90%"
            >
              <a-tooltip>
                <template #title>{{ exp.value }}</template>
                <a-select
                  v-if="exp.type == 'region'"
                  ref="select"
                  v-model:value="exp.value"
                  class="m-1"
                  style="
                    width: 220px;
                    border: 0.2px solid #5dc877;
                    color: #5dc877;
                  "
                  :disabled="exp.edit == 0"
                  @focus="focus"
                >
                  <a-select-option
                    v-for="option in staticObjectOptions"
                    :value="option.value"
                    >{{ option.label }}
                    <sup v-if="option.is_3d"> 3D</sup></a-select-option
                  >

                  <template
                    v-if="
                      index ==
                      expressions[process.name][step.name][substep].length - 1
                    "
                    #suffixIcon
                  >
                    <CloseOutlined
                      style="color: red"
                      @click="
                        removeOption(index, process.name, step.name, substep)
                      "
                    />
                  </template>
                </a-select>
              </a-tooltip>

              <a-tooltip>
                <template #title>{{ exp.value }}</template>
                <a-select
                  v-if="exp.type == 'object'"
                  ref="select"
                  v-model:value="exp.value"
                  class="m-1"
                  style="
                    width: 220px;
                    border: 0.2px solid #9accf2;
                    color: #9accf2;
                  "
                  :disabled="exp.edit == 0"
                  @focus="focus"
                >
                  <a-select-option
                    v-for="option in nonStaticObjectOptions"
                    :value="option.value"
                    >{{ option.label }}
                    <sup v-if="option.is_3d"> 3D</sup>
                  </a-select-option>
                  <template
                    v-if="
                      index ==
                      expressions[process.name][step.name][substep].length - 1
                    "
                    #suffixIcon
                  >
                    <CloseOutlined
                      style="color: red"
                      @click="
                        removeOption(index, process.name, step.name, substep)
                      "
                    />
                  </template>
                </a-select>
              </a-tooltip>

              <a-select
                v-if="exp.type == 'operator'"
                ref="select"
                v-model:value="exp.value"
                class="m-2"
                :disabled="exp.edit == 0"
                style="
                  border: 0.2px solid #f1ae00;
                  color: #f1ae00;
                  background-color: #f1ae00;
                "
                @focus="focus"
              >
                <a-select-option value="AND"> AND </a-select-option>
                <a-select-option value="OR"> OR </a-select-option>
                <a-select-option value="NOT"> NOT </a-select-option>
                <template
                  v-if="
                    index ==
                    expressions[process.name][step.name][substep].length - 1
                  "
                  #suffixIcon
                >
                  <CloseOutlined
                    style="color: red"
                    @click="
                      removeOption(index, process.name, step.name, substep)
                    "
                  />
                </template>
              </a-select>
              <a-input
                v-if="exp.type == 'time'"
                v-model:value="exp.value"
                :disabled="exp.edit == 0"
                type="number"
                placeholder="Action Time"
                style="border: 0.2px solid #ce15e7; padding: 5px; width: 100px"
                @focus="focus"
              >
                <template #prefix> FOR </template>
                <template
                  v-if="
                    index ==
                    expressions[process.name][step.name][substep].length - 1
                  "
                  #suffix
                >
                  <CloseOutlined
                    style="color: red"
                    @click="
                      removeOption(index, process.name, step.name, substep)
                    "
                  />
                </template>
              </a-input>
            </template>
          </div>
        </template>

        <a-divider />
      </div>
      <template #actions>
        <a-button
          type="primary"
          class="float-right mr-5"
          :loading="loading"
          :disabled="isDisable"
          @click="generateAssociateObjectJson"
        >
          Save
        </a-button>
      </template>
    </a-card>
  </div>
  <a-modal
    :visible="openCodeEditor"
    centered
    destroy-on-close
    :closable="false"
    :footer="null"
    :title="Translation"
    width="90vw"
    @cancel="closePopup"
  >
    <Editor
      :close="closePopup"
      v-bind:stepname="currentStep"
      :codevalue="customCodeByTask[currentStep]"
      :complete-code="completeCode"
      @saveCode="saveCode"
      @refresh-code="resetCode"
    ></Editor>
  </a-modal>
</template>

<script>
import { CloseOutlined } from '@ant-design/icons-vue';
import axios from 'axios';
import { objectAnnotationModes } from 'src/config/task-steps-objects.js';
import S3Service from 'src/services/s3';
import { mapActions, mapGetters } from 'vuex';
import Editor from './CodeEditor.vue';
import { codeFunctions, isDefaultObjects } from './config';

export default {
  components: {
    CloseOutlined,
    Editor,
  },
  inject: ['toast'],
  props: ['pipelineStep'],
  emits: ['nextStep'],

  setup() {
    return { objectAnnotationModes };
  },
  data() {
    return {
      expressions: {},
      loading: false,
      loadingCustomCode: false,
      verifiedSteps: [],
      is_verified: false,
      openCodeEditor: false,
      currentStep: '',
      customCode: '',
      customCodeByTask: {},
      stepname: [],
      completeCode: '',
      frameCounts: [],
      iouVlaue: [],
      patienceValue: [],
    };
  },

  computed: {
    ...mapGetters([
      'taskProcesses',
      'steps',
      'substeps',
      'stepsToIndexMapping',
      'annotationObjectJson',
      'stepsJsonData',
      'objectWarnings',
      'taskObjects',
      'organization',
      'selectedTask',
      'taskName',
      'advanceFeatures',
    ]),

    staticObjectOptions() {
      return this.taskObjects
        .filter(({ is_static }) => is_static === true)
        .map(({ id, name, is_3d }) => ({
          value: name,
          label: name,
          key: id,
          is_3d: is_3d,
        }));
    },

    nonStaticObjectOptions() {
      return this.taskObjects
        .filter(
          ({ is_static, name }) => is_static !== true && !isDefaultObjects(name)
        )
        .map(({ id, name, is_3d }) => ({
          value: name,
          label: name,
          key: id,
          is_3d: is_3d,
        }));
    },
    isDisable() {
      const steps = Object.keys(this.objectWarnings);
      if (!steps.length) return false;
      return steps.some((stepIndex) => this.objectWarnings[stepIndex] === true);
    },
  },

  async beforeMount() {
    console.log('this.taskProcesses', this.taskProcesses);
    this.changeAnnotationObjectTab(objectAnnotationModes['annotation']);
    this.setAnnotationObjectJson();
    this.customCodeByTask['__init__'] = '';
    this.taskProcesses.forEach((process) => {
      this.expressions[process.name] = {};
      process['steps'].forEach((step) => {
        this.expressions[process.name][step.name] = {};
        this.verifiedSteps.push([]);
        this.frameCounts.push([]);
        this.iouVlaue.push([]);
        this.patienceValue.push([]);
        step['substeps'].forEach((substep) => {
          this.expressions[process.name][step.name][substep] = [];
          this.customCodeByTask[substep] = '';
          this.stepname.push(substep);
        });
      });
    });

    if (this.stepsJsonData) {
      let key = Object.keys(this.stepsJsonData)[0];
      if (
        key != null &&
        this.stepsJsonData[key].hasOwnProperty('rule_type') &&
        this.stepsJsonData[key]['rule_type']
      ) {
        this.generateOldExpressionfromStepData();
      } else {
        this.generateExpressionfromStepData();
      }
    } else this.generateExpressionfromStepData();

    await this.readCodeFromFile();
    this.extractCustomCodeLines(this.completeCode);
    this.parseCustomCodeTaskViseByFunction();
    // this.reOrder();
  },

  methods: {
    ...mapActions([
      'setAnnotationObjectJson',
      'changeAnnotationObjectTab',
      'setStepsJsonData',
      'createStepsJsonData',
    ]),

    getSubstepNames() {
      let substepNames = ['__init__', 'reset', 'draw'];
      this.taskProcesses.forEach((process) => {
        process['steps'].forEach((step) => {
          step['substeps'].forEach((substep) => {
            substepNames.push(substep);
          });
        });
      });
      return substepNames;
    },

    initializeCustomCodeByTask(substepNames) {
      this.customCodeByTask = {};
      this.customCodeByTask['__init__'] = '';
      this.customCodeByTask['reset'] = '';
      this.customCodeByTask['draw'] = '';
      substepNames.forEach((substep) => {
        this.customCodeByTask[substep] = '';
      });
    },

    getSearchQuery(substep) {
      let query = this.getFunctionName(substep);
      if (!codeFunctions.some((item) => item === substep)) {
        query = 'def detector_' + query + '(';
      }
      return query;
    },

    getCurrentSubStep(index, lines) {
      let currentSubstep = '';
      this.getSubstepNames().forEach((substep) => {
        if (lines[index].includes(this.getSearchQuery(substep))) {
          currentSubstep = substep;
          return;
        }
      });
      return currentSubstep;
    },

    isFunctionLine(index, lines) {
      return (
        lines[index].substring(0, 13) === 'def detector_' ||
        lines[index].substring(0, 9) === 'def reset' ||
        lines[index].substring(0, 9) === 'def draw('
      );
    },

    isNextFunctionOrEndOfLine(index, lines) {
      return (
        index == lines.length - 1 || lines[index].substring(0, 3) === 'def'
      );
    },

    parseCustomCodeTaskViseByFunction() {
      const substepNames = this.getSubstepNames();
      this.initializeCustomCodeByTask(substepNames);

      const lines = this.customCode.split('\n');
      let startParsing = '';
      let currentStep = '';
      let flag = false;

      for (let index = 0; index < lines.length; index++) {
        if (this.isFunctionLine(index, lines) && !flag) {
          flag = true;
          if (startParsing == '') startParsing += lines[index];

          currentStep = this.getCurrentSubStep(index, lines);
          if (currentStep === '') {
            startParsing = '';
            flag = false;
          }
        } else if (this.isNextFunctionOrEndOfLine(index, lines) && flag) {
          if (index == lines.length - 1)
            startParsing = startParsing + '\n' + lines[index];

          this.customCodeByTask[currentStep] = startParsing;
          startParsing = '';
          currentStep = '';
          flag = false;
          if (lines[index].substring(0, 3) === 'def') index -= 1;
        } else if (flag) {
          startParsing = startParsing + '\n' + lines[index];
        }
      }
    },

    parseCustomCodeTaskVise() {
      const substepNames = this.getSubstepNames();
      this.initializeCustomCodeByTask(substepNames);

      const lines = this.customCode.split('\n');
      let startParsing = '';
      let currentStep = '';
      let flag = -1;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes('# Custom') && flag == -1) {
          flag = 1;
          if (startParsing == '') startParsing += lines[i];
          else startParsing = startParsing + '\n' + lines[i];
          substepNames.forEach((substep) => {
            if (lines[i].includes(substep)) {
              currentStep = substep;
            }
          });
        } else if (
          (lines[i].includes('# Custom') || i == lines.length - 1) &&
          flag == 1
        ) {
          if (i == lines.length - 1)
            startParsing = startParsing + '\n' + lines[i];
          this.customCodeByTask[currentStep] = startParsing;
          flag = -1;
          startParsing = '';
          currentStep = '';
          i -= 1;
          continue;
        } else {
          if (startParsing == '') startParsing += lines[i];
          else startParsing = startParsing + '\n' + lines[i];
        }
      }
    },

    getFunctionName(step) {
      let functionName = '';
      step.split(' ').forEach((value) => {
        if (functionName.length == 0) functionName = value;
        else functionName = functionName + '_' + value;
      });
      return functionName.replace(/[^a-zA-Z0-9-]/g, '_').replace(/-/g, '_');
    },

    async readCodeFromFile() {
      this.loadingCustomCode = true;
      const bucket = `${this.organization}-training`;
      const filePath = `${this.taskName}/SwappableObjectDetector/custom_code.py`;

      let [error, data] = await S3Service.readFile({
        bucket: bucket,
        file_path: filePath,
      });
      this.customCode = data;
      if (data.error == 'NoSuchKey' || data.length < 2) {
        this.generateCompleteCode();
        this.loadingCustomCode = false;
        return;
      }
      // let lines = data.split('\n');
      // for (let i = 1; i < lines.length; i++) {
      //   this.customCode += lines[i].slice(2) + '\n';
      // }
      //       // Check if `reset` is missing
      //       if (!this.customCode.includes('def reset')) {
      //         this.customCode += `
      // # Custom function for reset
      // def reset(self):
      //   # write your code here!
      //   pass\n
      // `;
      //       }
      //       if (!this.customCode.includes('def draw_objects')) {
      //         this.customCode += `
      // # Custom function for draw_objects
      // def draw_objects(self, frame, classification_point):
      //   # write your code here!
      //   return frame \n
      // `;
      //       }

      this.loadingCustomCode = false;

      const regex = /def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g;
      let existingFunctions = [];
      let match;
      function toHumanReadable(functionName) {
        // Remove 'detector_' prefix and then convert to human-readable format
        return functionName
          .replace(/^detector_/, '') // Remove the 'detector_' prefix
          .replace(/_/g, '-'); // Replace underscores with spaces
      }
      function ensureDetectorPrefix(functionName) {
        functionName = functionName.replace(/[\s-]+/g, '_');
        if (!functionName.startsWith('detector_')) {
          functionName = functionName.replace(/\s+/g, '_');
          return `detector_${functionName}`;
        }
      }
      while ((match = regex.exec(data)) !== null) {
        // Exclude 'get_predictions'
        if (!codeFunctions.some((item) => item === match[1])) {
          const humanReadableName = toHumanReadable(match[1]);
          existingFunctions.push(humanReadableName);
        }
      }
      let missingFunctions = this.stepname.filter(
        (func) => !existingFunctions.includes(func)
      );
      let deletedFunctions = existingFunctions.filter(
        (func) => !this.stepname.includes(func)
      );
      console.log('missingFunctions', missingFunctions);
      console.log('deletedFunctions', deletedFunctions);
      if (deletedFunctions.length > 0) {
        let currentCode = this.customCode.split('\n');
        console.log('data', data);
        currentCode = currentCode.join('\n'); // Start with the original data as the base code
        console.log('currentCode', currentCode);
        let isFirstIteration = true;
        deletedFunctions.forEach((funcName) => {
          let lines = currentCode.split('\n'); // Split the current code into lines
          let newCustomCode = ''; // Initialize a new string to hold the modified code
          const targetComment = `def ${ensureDetectorPrefix(
            funcName
          )}(self, current_detection, classification_point):`; // Define the target comment
          console.log('targetComment', targetComment);
          let isDeleting = false; // A flag to determine if we are inside the function to be deleted

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Check if the line matches the target comment
            if (line.includes(targetComment)) {
              isDeleting = true; // Start deleting the function
              continue;
            }

            // If currently deleting, check for the end of the function
            if (isDeleting) {
              if (
                (line.includes('def detector_') &&
                  line.trim() !== targetComment.trim()) ||
                (line.includes('def get_predictions') &&
                  line.trim() !== targetComment.trim())
              ) {
                console.log('rafayy', line);
                console.log('rafayy_target_comment', targetComment);
                console.log('rafayy_include', line.includes('def detector_'));
                console.log('rafayy_target', line !== targetComment);
                isDeleting = false; // Stop deleting when another function block is found
                if (isFirstIteration) {
                  if (!line.startsWith(' ')) {
                    newCustomCode += line + '\n';
                  } else {
                    newCustomCode += line + '\n';
                  }
                } else {
                  newCustomCode += line + '\n'; // For subsequent iterations, do not slice
                }
              }
              console.log('rafayy_delete', line);
              continue;
            }
            if (isFirstIteration) {
              if (!line.startsWith(' ')) {
                newCustomCode += line + '\n';
              } else {
                newCustomCode += line + '\n';
              }
            } else {
              newCustomCode += line + '\n'; // For subsequent iterations, do not slice
            }
          }

          // Update the current code after processing each function
          isFirstIteration = false;
          currentCode = newCustomCode;
        });
        console.log('currentCode deletre', currentCode);
        let lines = currentCode.split('\n');
        this.customCode = '';
        for (let i = 0; i < lines.length; i++) {
          this.customCode += lines[i] + '\n';
        }
        this.loadingCustomCode = false;
        this.completeCode = currentCode;
        // data = this.customCode;
        // this.extractCustomCodeLines(data);
      }

      if (missingFunctions.length > 0) {
        let currentCode = this.customCode.split('\n');
        // if (deletedFunctions.length > 0) {
        //   currentCode = this.customCode.split('\n');
        // } else {
        //   currentCode = data.split('\n');
        // }
        currentCode = currentCode.join('\n'); // Start with the original data as the base code
        let isFirstIteration = true;
        missingFunctions.forEach((func) => {
          // Create a new function template to insert
          let updatedFuncName = ensureDetectorPrefix(func);
          let newFunctionCode = `
  def ${updatedFuncName}(self, current_detection, classification_point):
      # write your code here!
      return False
`;
          // Flag to ensure slice(2) happens only once
          let lines = currentCode.split('\n'); // Always split from the current state of the code
          let newCustomCode = '';

          for (let i = 0; i < lines.length; i++) {
            if (
              lines[i].includes(
                'def get_predictions(self, current_detection, classification_point):'
              )
            ) {
              // Add the new function code before the matching line
              newCustomCode += newFunctionCode + '\n';
            }

            if (isFirstIteration) {
              if (!lines[i].startsWith(' ')) {
                newCustomCode += lines[i] + '\n';
              } else {
                newCustomCode += lines[i] + '\n';
              }
            } else {
              newCustomCode += lines[i] + '\n'; // For subsequent iterations, do not slice
            }
          }

          isFirstIteration = false; // Ensure slice(2) is not applied in subsequent function additions
          currentCode = newCustomCode; // Update the current code
        });
        console.log('currentCodeRafay', currentCode);
        this.customCode = '';
        let lines = currentCode.split('\n');
        for (let i = 0; i < lines.length; i++) {
          this.customCode += lines[i] + '\n';
        }
        this.completeCode = currentCode;
        this.loadingCustomCode = false;
      }

      console.log('customCodeByTask', this.customCodeByTask);
      console.log('this.customCode', this.customCode);

      if (deletedFunctions.length == 0 && missingFunctions.length == 0) {
        this.extractCustomCodeLines(data);
        // let lines = data.split('\n');
        // for (let i = 1; i < lines.length; i++) {
        //   this.customCode += lines[i].slice(2) + '\n';
        // }
        // // this.customCode = data;
        // this.loadingCustomCode = false;
        // // this.parseCustomCodeTaskVise();
      }
      console.log('this.customCode', this.completeCode);
    },
    reOrder() {
      this.customCode = '';
      let custom_code = this.customCodeByTask;
      let code_lines = custom_code['__init__'] + '\n\n';
      this.stepname.forEach((step) => {
        code_lines += custom_code[step] + '\n';
      });
      code_lines += custom_code[''];
      let lines = code_lines.split('\n');
      for (let i = 0; i < lines.length; i++) {
        this.customCode += lines[i] + '\n';
      }
    },

    extractCustomCodeLines(data) {
      const lines = data.split('\n');
      this.customCode = '';
      this.completeCode = data;
      const customCommentIndex = lines.findIndex((item) =>
        item.includes('class')
      );

      if (customCommentIndex === -1) return;
      for (let index = customCommentIndex + 1; index < lines.length; index++) {
        this.customCode += lines[index].slice(2) + '\n';
      }
    },

    addClassInCustomCode(data) {
      const importStatement =
        'from Utils.SharedVars.shared_vars import context\nimport cv2\n';
      let customCodeModule = importStatement + '\nclass CustomCode: \n';
      let code = data.split('\n');
      for (let line = 0; line < code.length; line++) {
        if (code[line].includes('self.updated_time')) {
          code[line] = `  self.updated_time = "${new Date().toISOString()}"`;
        }
      }
      let _ = code.join('\n');
      _.split('\n').forEach((line) => {
        customCodeModule += '  ' + line + '\n';
      });
      this.completeCode = customCodeModule;
    },

    getPayload() {
      const bucket = `${this.organization}-training`;
      const filePath = `${this.taskName}/SwappableObjectDetector/`;
      const payload = new FormData();

      payload.append('bucket', bucket);
      payload.append('file_path', filePath);
      payload.append('file');
      return payload;
    },
    async getPresignUrl() {
      let payload = {
        bucket: `${this.organization}-training`,
        key: `${this.taskName}/SwappableObjectDetector/custom_code.py`,
      };
      const [error, data] = await S3Service.getPresignedPostURL(payload, false);
      return error ? error : data;
    },
    handleKeyDownThreshold(event) {
      // Allow arrow keys and prevent other manual text inputs
      const allowedKeys = ['ArrowUp', 'ArrowDown', 'Backspace', 'Delete', '.'];
      const isDigit = /^[0-9]$/.test(event.key);
      if (!allowedKeys.includes(event.key) && !isDigit) {
        event.preventDefault();
      }
    },

    async handleKeyDownNumber(event) {
      // Allow arrow keys and prevent other manual text inputs
      const allowedKeys = ['ArrowUp', 'ArrowDown', 'Backspace', 'Delete'];
      const isDigit = /^[0-9]$/.test(event.key);

      if (!allowedKeys.includes(event.key) && !isDigit) {
        event.preventDefault();
      }
    },
    sanitizeNumberInput(stepIndex, substepIndex, type) {
      let targetArray =
        type === 'frames'
          ? this.frameCounts
          : type === 'patience'
          ? this.patienceValue
          : this.iouVlaue;

      let currentValue = targetArray[stepIndex]?.[substepIndex];

      // Ensure integer and minimum value of 1
      targetArray[stepIndex][substepIndex] = currentValue;
    },
    async createAWSFormData(data) {
      let awsformData = new FormData();
      Object.keys(data.fields).forEach((key) => {
        awsformData.append(key, data.fields[key]);
      });

      const blob = new Blob([this.completeCode], { type: 'text/python' });
      const file = new File([blob], 'custom_code.py', { type: 'text/python' });

      awsformData.append('file', file);
      return awsformData;
    },
    async saveFileViaPresignedURL() {
      const data = await this.getPresignUrl();
      if (!data) {
        this.toast.error('Could not access storage');
        return;
      }
      let awsformData = await this.createAWSFormData(data);
      await this.uploadFileToS3(data.url, awsformData);
    },
    async uploadFileToS3(url, data) {
      const headers = {
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
      };
      await axios.post(url, data, {
        headers,
      });
    },
    async upload() {
      await this.saveFileViaPresignedURL();
    },

    async saveCode(code, stepname) {
      try {
        let codeScript =
          stepname === '__init__' ? code : this.parseSaveCode(code, stepname);
        this.extractCustomCodeLines(codeScript);
        this.parseCustomCodeTaskViseByFunction();
      } catch (error) {
        console.error(error);
      }
    },

    pushCode(code, stepname) {
      this.customCodeByTask[stepname] = code;
      const detectorCode = code.split('\n');
      const lines = this.completeCode.split('\n');

      let firstPortion = '';
      for (let index = 0; index < lines.length; index++)
        firstPortion += lines[index] + '\n';

      let parser = '';
      for (let index = 0; index < detectorCode.length; index++)
        parser = parser + '  ' + detectorCode[index] + '\n';

      return firstPortion + parser;
    },

    parseSaveCode(code, stepname) {
      if (!this.customCodeByTask[stepname]) {
        return this.pushCode(code, stepname);
      }

      this.customCodeByTask[stepname] = code;
      const lines = this.completeCode.split('\n');
      const detectorCode = code.split('\n');
      let parser = '';
      for (let index = 0; index < detectorCode.length; index++)
        parser = parser + '  ' + detectorCode[index] + '\n';

      let query = `def detector_${this.getFunctionName(stepname)}`;
      if (stepname === 'reset' || stepname === 'draw')
        query = `def ${stepname}`;

      const elementIndex = lines.findIndex((item) => {
        const functionName = item.replace(/^\s+/, '');
        return functionName.substring(0, query.length + 1) === query + '(';
      });

      let firstPortion = '';
      for (let index = 0; index < elementIndex; index++)
        firstPortion += lines[index] + '\n';

      let script = firstPortion + parser;
      const secondPortionIndex = this.getNextFunctionStartIndex(
        elementIndex,
        lines
      );
      if (secondPortionIndex === -1) return script;

      for (let index = secondPortionIndex; index < lines.length; index++) {
        script += lines[index] + '\n';
      }
      return script;
    },

    getNextFunctionStartIndex(elementIndex, lines) {
      const skipCurrentFunctionIndex = 1;
      for (
        let index = elementIndex + skipCurrentFunctionIndex;
        index < lines.length;
        index++
      ) {
        const line = lines[index].slice(2);
        if (line.substring(0, 3) === 'def' || index === lines.length - 1) {
          return index;
        }
      }
      return -1;
    },

    openPopup(substep) {
      if (substep == null) {
        this.customCode = this.openCodeEditor = true;
      } else {
        this.currentStep = substep;
        this.openCodeEditor = true;
      }
    },
    closePopup() {
      this.openCodeEditor = false;
    },
    validateExpressionOnSave() {
      let valid = true;

      this.taskProcesses.forEach((process) => {
        process['steps'].forEach((step) => {
          step['substeps'].forEach((substep) => {
            var exp = this.expressions[process.name][step.name][substep];
            if (exp.length != 0 && exp[exp.length - 1].type == 'operator') {
              this.toast.error(
                `Invalid Step (${step.name}) : ` +
                  'Operator cannot be last item.'
              );
              valid = false;
            }

            // only AND NOT and OR NOT allowed
            for (let i = 0; i < exp.length; i++) {
              if (i + 1 == exp.length) break;
              if (
                exp[i + 1].type == 'operator' &&
                exp[i + 1].type == exp[i].type &&
                exp[i + 1].value != 'NOT'
              ) {
                valid = false;
                this.toast.error(
                  `Invalid Step (${step.name}) : ` +
                    "Cannot use 'AND' and 'OR' operators consecutively."
                );
                break;
              }
            }

            // invalid not operator

            for (let i = 0; i < exp.length; i++) {
              if (exp[i].type == 'operator' && exp[i].value == 'NOT') {
                if (i != 0 && exp[i - 1].type != 'operator') {
                  valid = false;
                  this.toast.error(
                    `Invalid Step (${step.name}) : ` +
                      "Incorrect usage of 'NOT' operator."
                  );
                  break;
                } else if (i != 0 && exp[i - 1].value == 'NOT') {
                  valid = false;
                  this.toast.error(
                    `Invalid Step (${step.name}) : ` +
                      "Incorrect usage of 'NOT' operator."
                  );
                  break;
                }
              }
            }

            // invalid time operator
            if (exp.length > 0 && exp[0].type == 'time') {
              valid = false;
              this.toast.error(
                `Invalid Step (${step.name}) : ` +
                  "Incorrect usage of 'Time' operator."
              );
            }
            for (let i = 0; i < exp.length; i++) {
              if (exp[i].type == 'time') {
                if (i - 1 >= 0 && exp[i - 1].type == 'operator') {
                  valid = false;
                  this.toast.error(
                    `Invalid Step (${step.name}) : ` +
                      "Incorrect usage of 'Time' operator."
                  );
                  break;
                }
                if (i + 1 != exp.length && exp[i + 1].type != 'operator') {
                  valid = false;
                  this.toast.error(
                    `Invalid Step (${step.name}) : ` +
                      "Incorrect usage of 'Time' operator."
                  );
                  break;
                }
              }
            }

            // region should have an object
            for (let i = 0; i < exp.length; i++) {
              if (exp[i].type === 'region') {
                if (
                  (i === 0 || exp[i - 1].type !== 'object') &&
                  (i === exp.length - 1 || exp[i + 1].type !== 'object')
                ) {
                  valid = false;
                  this.toast.error(
                    `Invalid Step (${step.name}) : ` +
                      'Please choose an object for the step.'
                  );
                  break;
                }
              }
            }
          });
        });
      });
      return valid;
    },
    validation(expression, newExp) {
      if (
        expression.length != 0 &&
        expression[expression.length - 1].type == 'region' &&
        newExp == 'region'
      ) {
        this.toast.error('Interaction not possible.');

        return false;
      }
      if (
        expression.length - 3 >= 0 &&
        expression[expression.length - 1].type != 'operator' &&
        expression[expression.length - 2].type != 'operator' &&
        expression[expression.length - 3].type != 'operator' &&
        newExp != 'operator'
      ) {
        this.toast.error('Interaction not possible.');
        return false;
      }

      return true;
    },
    addExpression(newExp, processName, stepName, subStepName) {
      // validation
      let expression = this.expressions[processName][stepName][subStepName];

      if (!this.validation(expression, newExp)) {
        return;
      }
      if (newExp == 'object') {
        this.expressions[processName][stepName][subStepName].push({
          type: newExp,
          value: this.nonStaticObjectOptions[0].value,
          edit: 1,
        });
      } else if (newExp == 'region') {
        this.expressions[processName][stepName][subStepName].push({
          type: newExp,
          value: this.staticObjectOptions[0].value,
          edit: 1,
        });
      } else if (newExp == 'time') {
        this.expressions[processName][stepName][subStepName].push({
          type: newExp,
          value: 5,
          edit: 1,
        });
      } else {
        for (
          let i = 0;
          i < this.expressions[processName][stepName][subStepName].length;
          i++
        ) {
          this.expressions[processName][stepName][subStepName][i]['edit'] = 0;
        }
        this.expressions[processName][stepName][subStepName].push({
          type: newExp,
          value: 'AND',
          edit: 1,
        });
      }
    },
    removeOption(index, processName, stepName, subStepName) {
      this.expressions[processName][stepName][subStepName].splice(index, 1);
      this.expressions[processName][stepName][subStepName][
        this.expressions[processName][stepName][subStepName].length - 1
      ].edit = 1;
    },
    async generateAssociateObjectJson() {
      this.loading = true;
      if (!this.validateExpressionOnSave()) {
        this.loading = false;
        return;
      }

      let stepdata = this.generateStepsData();

      this.setStepsJsonData(stepdata);
      await this.upload();
      await this.createStepsJsonData(stepdata);

      this.toast.success('Association Saved');
      this.loading = false;
    },

    validateAnnotationObjects() {
      const steps_data = this.annotationObjectJson['annotation'];
      return Object.values(steps_data).some(
        ({ static_object, non_static_object }) =>
          static_object && !non_static_object.length
      );
    },

    generateOldExpressionfromStepData() {
      let stepdata = this.stepsJsonData;
      if (!stepdata) return;
      let stepExpression = [];
      for (var key of Object.keys(stepdata)) {
        let rule_type = stepdata[key]['rule_type'];
        if (!rule_type) return;
        let detectionType = Object.keys(rule_type)[0];
        let objects = rule_type[detectionType]['objects'];
        let region = rule_type[detectionType]['region'];
        let expressionFound = [];

        if (objects.length > 1) {
          // 2 objects
          expressionFound.push({
            type: 'object',
            value: objects[0],
            edit: 1,
          });
          expressionFound.push({
            type: 'object',
            value: objects[1],
            edit: 1,
          });
        } else if (
          (region == null || region.length == 0) &&
          objects.length == 1
        ) {
          // 1 object
          expressionFound.push({
            type: 'object',
            value: objects[0],
            edit: 1,
          });
        } else {
          // object region
          expressionFound.push({
            type: 'object',
            value: objects[0],
            edit: 1,
          });
          expressionFound.push({
            type: 'region',
            value: region,
            edit: 1,
          });
        }
        stepExpression.push(expressionFound);
        let stepIndex = 0;
        this.taskProcesses.forEach((process) => {
          process['steps'].forEach((step) => {
            step['substeps'].forEach((substep) => {
              this.expressions[process.name][step.name][substep] =
                stepExpression[stepIndex];
              stepIndex += 1;
            });
          });
        });
      }
    },
    generateExpressionfromStepData() {
      let stepdata = this.stepsJsonData;
      let stepExpression = [];
      let verifiedStepDate = [];
      let frameCountsData = [];
      let iouValueData = [];
      let patienceValueData = [];
      if (stepdata) {
        for (var key of Object.keys(stepdata)) {
          let boolean_expr = stepdata[key]['boolean_expr'];
          verifiedStepDate.push(!stepdata[key]['detect_always']);
          frameCountsData.push(stepdata[key]['frames'] ?? 5);
          iouValueData.push(stepdata[key]['threshold_iou_value'] ?? 0.1);
          patienceValueData.push(stepdata[key]['patience_values'] ?? 0);
          if (boolean_expr.length == 0 || boolean_expr == null) {
            stepExpression.push([]);
            continue;
          }
          let rule_types = stepdata[key]['rule_types'];
          let values = boolean_expr.split(' ');

          let expressionFound = [];
          values.forEach((value) => {
            if (value.includes('FOR_')) {
              let timeFormat = value.split('_');
              expressionFound.push({
                type: 'time',
                value: timeFormat[timeFormat.length - 1],
                edit: 1,
              });
            } else if (['AND', 'OR', 'NOT', 'FOR_'].includes(value)) {
              if (value.includes('FOR_')) {
                let timeFormat = value.split('_');
                expressionFound.push({
                  type: 'time',
                  value: timeFormat[timeFormat.length - 1],
                  edit: 1,
                });
              }
              expressionFound.push({
                type: 'operator',
                value: value,
                edit: 1,
              });
            } else {
              let objects = rule_types[value]['objects'];
              let region = rule_types[value]['region'];

              if (objects.length > 1) {
                // 2 objects
                expressionFound.push({
                  type: 'object',
                  value: objects[0],
                  edit: 1,
                });
                expressionFound.push({
                  type: 'object',
                  value: objects[1],
                  edit: 1,
                });
              } else if (region.length == 0 && objects.length == 1) {
                // 1 object
                expressionFound.push({
                  type: 'object',
                  value: objects[0],
                  edit: 1,
                });
              } else {
                // object region
                expressionFound.push({
                  type: 'object',
                  value: objects[0],
                  edit: 1,
                });
                expressionFound.push({
                  type: 'region',
                  value: region,
                  edit: 1,
                });
              }
            }
          });
          stepExpression.push(expressionFound);
        }
      }
      let stepIndex = 0;
      this.taskProcesses.forEach((process) => {
        process['steps'].forEach((step, step_Index) => {
          step['substeps'].forEach((substep, substep_Index) => {
            this.verifiedSteps[step_Index][substep_Index] =
              verifiedStepDate[stepIndex];
            this.frameCounts[step_Index][substep_Index] =
              frameCountsData[stepIndex] ?? 5;
            this.iouVlaue[step_Index][substep_Index] =
              iouValueData[stepIndex] ?? 0.1;
            this.patienceValue[step_Index][substep_Index] =
              patienceValueData[stepIndex] ?? 0;

            let is_bool = true;
            if (stepExpression.hasOwnProperty(stepIndex.toString())) {
              for (let i = stepExpression[stepIndex].length - 1; i >= 0; i--) {
                if (
                  ['AND', 'OR'].includes(stepExpression[stepIndex][i].value)
                ) {
                  is_bool = false;
                }
                if (!is_bool) {
                  stepExpression[stepIndex][i].edit = 0;
                }
              }
              this.expressions[process.name][step.name][substep] =
                stepExpression[stepIndex];
            }
            stepIndex += 1;
          });
        });
      });
    },
    generateStepsData() {
      let stepdata = {};
      let stepIndex = 0;
      this.taskProcesses.forEach((process) => {
        process['steps'].forEach((step, step_index) => {
          step['substeps'].forEach((substep, substep_index) => {
            const verified =
              this.verifiedSteps[step_index][substep_index] == null
                ? false
                : this.verifiedSteps[step_index][substep_index];
            const frames =
              this.frameCounts[step_index][substep_index] == null
                ? 5
                : this.frameCounts[step_index][substep_index];
            const iouVlaues =
              this.iouVlaue[step_index][substep_index] == null
                ? 0.1
                : this.iouVlaue[step_index][substep_index];
            const patienceValues =
              this.patienceValue[step_index][substep_index] == null
                ? 0
                : this.patienceValue[step_index][substep_index];
            const curr_expression =
              this.expressions[process.name][step.name][substep];
            var rule_types = {};
            var boolean_expression = '';
            // rule type and boolean expression
            var operator_split_expression = [];
            var temp_arr = [];

            for (let j = 0; j < curr_expression.length; j++) {
              if (j == curr_expression.length - 1) {
                temp_arr.push(curr_expression[j]);
                operator_split_expression.push(temp_arr);
                temp_arr = [];
              }
              if (['AND', 'OR', 'NOT'].includes(curr_expression[j].value)) {
                operator_split_expression.push(temp_arr);
                operator_split_expression.push([curr_expression[j]]);
                temp_arr = [];
              } else {
                temp_arr.push(curr_expression[j]);
              }
            }
            let rule_type_object_key = '';
            let rule_type_name = '';
            let time = '';
            let objects_present = [];
            for (let j = 0; j < operator_split_expression.length; j++) {
              if (operator_split_expression[j].length === 0) {
                continue;
              }
              if (Object.keys(operator_split_expression[j]).length === 0) {
                continue;
              }
              if (
                operator_split_expression[j][
                  operator_split_expression[j].length - 1
                ].type == 'time'
              ) {
                time =
                  'FOR_' +
                  operator_split_expression[j][
                    operator_split_expression[j].length - 1
                  ].value +
                  ' ';
              }

              if (
                operator_split_expression[j][0].type === 'operator' &&
                operator_split_expression[j][0].value != 'NOT'
              ) {
                boolean_expression =
                  boolean_expression +
                  rule_type_object_key +
                  ' ' +
                  time +
                  operator_split_expression[j][0].value +
                  ' ';
                time = '';
                continue;
              }
              if (
                operator_split_expression[j][0].type === 'operator' &&
                operator_split_expression[j][0].value == 'NOT'
              ) {
                boolean_expression =
                  boolean_expression +
                  operator_split_expression[j][0].value +
                  ' ';
                continue;
              }

              rule_type_name = '';
              rule_type_object_key = '';

              if (
                (operator_split_expression[j].length < 2 ||
                  operator_split_expression[j][1].type == 'time') &&
                operator_split_expression[j][0].type == 'object'
              ) {
                rule_type_name = 'object-detection';
                rule_type_object_key = (
                  operator_split_expression[j][0].value +
                  '_' +
                  rule_type_name
                ).replace(/ /g, '_');
              } else if (
                (operator_split_expression[j][0].type == 'object' &&
                  operator_split_expression[j][1].type == 'object') ||
                (operator_split_expression[j][1].type == 'object' &&
                  operator_split_expression[j][0].type == 'object')
              ) {
                if (
                  operator_split_expression[j][0].value == 'hands' ||
                  operator_split_expression[j][1].value == 'hands'
                )
                  rule_type_name = 'hand-object-interaction';
                else rule_type_name = 'object-object-interaction';

                rule_type_object_key = (
                  operator_split_expression[j][0].value +
                  '_' +
                  operator_split_expression[j][1].value +
                  '_' +
                  rule_type_name
                ).replace(/ /g, '_');
              } else if (
                (operator_split_expression[j][0].type == 'object' &&
                  operator_split_expression[j][1].type == 'region') ||
                (operator_split_expression[j][1].type == 'object' &&
                  operator_split_expression[j][0].type == 'region')
              ) {
                if (
                  operator_split_expression[j][0].value == 'hands' ||
                  operator_split_expression[j][1].value == 'hands'
                )
                  rule_type_name = 'hand-region-interaction';
                else rule_type_name = 'region-based-detection';

                if (operator_split_expression[j][0].type == 'object')
                  rule_type_object_key = (
                    operator_split_expression[j][0].value +
                    '_' +
                    operator_split_expression[j][1].value +
                    '_' +
                    rule_type_name
                  ).replace(/ /g, '_');
                else
                  rule_type_object_key = (
                    operator_split_expression[j][1].value +
                    '_' +
                    operator_split_expression[j][0].value +
                    '_' +
                    rule_type_name
                  ).replace(/ /g, '_');
              }

              if (j == operator_split_expression.length - 1) {
                boolean_expression = boolean_expression + rule_type_object_key;
                if (
                  operator_split_expression[j][
                    operator_split_expression[j].length - 1
                  ].type == 'time'
                ) {
                  boolean_expression =
                    boolean_expression +
                    ' FOR_' +
                    operator_split_expression[j][
                      operator_split_expression[j].length - 1
                    ].value +
                    ' ';
                }
              }

              // objects
              var objects = [];
              var region = '';
              operator_split_expression[j].forEach((obj) => {
                if (obj.type == 'object') objects.push(obj.value);
                if (obj.type == 'region') region = obj.value;
              });

              objects_present = [...objects_present, ...objects];

              rule_types[rule_type_object_key] = {
                objects: objects,
                rule_type_name: rule_type_name,
                region: region,
                condition: {
                  threshold_value: iouVlaues,
                },
              };
            }
            let prev_stepdata = this.stepsJsonData;

            if (
              prev_stepdata &&
              prev_stepdata.hasOwnProperty(stepIndex.toString()) &&
              prev_stepdata[stepIndex.toString()].hasOwnProperty(
                'step_relations'
              ) &&
              prev_stepdata[stepIndex.toString()] != null &&
              prev_stepdata[stepIndex.toString()] != 'undefined'
            ) {
              stepdata[stepIndex.toString()] = {
                objects_present: [...new Set(objects_present)],
                detect_always: !verified,
                frames: frames,
                patience_values: patienceValues,
                boolean_expr: boolean_expression.trim(),
                rule_types: rule_types,
                step_relations:
                  prev_stepdata[stepIndex.toString()]['step_relations'],
              };
            } else {
              stepdata[stepIndex.toString()] = {
                objects_present: [...new Set(objects_present)],
                detect_always: !verified,
                frames: frames,
                patience_values: patienceValues,
                boolean_expr: boolean_expression.trim(),
                rule_types: rule_types,
                step_relations: {
                  'anchor-relation': {
                    key_steps: [],
                    before_anchor: [],
                    after_anchor: [],
                  },
                  'similar-relation': {
                    key_steps: [],
                    before_anchor: [],
                    after_anchor: [],
                  },
                },
              };
            }

            stepIndex += 1;
          });
        });
      });

      return stepdata;
    },

    removeEmptyAnnotationObj(json) {
      const steps_data = this.annotationObjectJson['annotation'];
      if (json) {
        Object.keys(json).forEach((sIdx) => {
          const tempStepData = steps_data[sIdx];
          if (
            !tempStepData['non_static_object']?.length >= 1 &&
            !tempStepData['static_object'] &&
            !json[sIdx].hasOwnProperty('verify_step')
          ) {
            delete json[sIdx];
          }
        });
      }
      return json;
    },

    removeEmptySteps(json) {
      if (json) {
        Object.keys(json).forEach((sIdx) => {
          if (
            json.hasOwnProperty(sIdx.toString()) &&
            !Object.keys(json[sIdx.toString()]).length
          ) {
            delete json[sIdx];
          }
        });
      }
      return json;
    },

    generateCompleteCode() {
      let Codedata =
        `def __init__(self, detection):
  # write your code here!
  self.update_time = "${new Date().toISOString()}"
  self.detection = detection
  self.global_access = context.context_dict\n


def reset(self):
  # write your code here!
  pass\n

def draw_polygon(self, frame, detection, color = (0, 0, 255), label = None):
    obb_detection = isinstance(detection[0],tuple)
    if obb_detection:
        (x1, y1), (x2, y2), (x3, y3), (x4, y4), conf = detection
        pts = np.array([(x1, y1), (x2, y2), (x3, y3), (x4, y4)], np.int32).reshape((-1, 1, 2))
        cv2.polylines(frame, [pts], isClosed=True, color=color, thickness=2)
        if label:
            cv2.putText(frame, str(label), (pts[0][0][0] -5 , pts[0][0][1]),
                        cv2.FONT_HERSHEY_SIMPLEX, 0.5, (0, 0, 0), 1)
    else:
        x1, y1, x2, y2, conf = detection
        box_w, box_h = int(x2 - x1), int(y2 - y1)
        cv2.rectangle(frame, (abs(int(x1)), abs(int(y1))), (abs(int(x1) + box_w), abs(int(y1) + box_h)),
                      color, 2)

        if label:
            cv2.putText(frame, label, (abs(int(x1 - 5)), abs(int(y1))), cv2.FONT_HERSHEY_SIMPLEX, 0.5, color,
                        1)

def blur_face(self, frame, detection, ksize = (23, 23), sigmaX = 30):
    obb_detection = isinstance(detection[0],tuple)
    if obb_detection:
        (x1, y1), (x2, y2), (x3, y3), (x4, y4), conf = detection
        pts = np.array([(x1, y1), (x2, y2), (x3, y3), (x4, y4)], np.int32).reshape((-1, 1, 2))

        # Create a mask
        mask = np.zeros_like(frame, dtype=np.uint8)

        # Fill the OBB area with white (255)
        cv2.fillPoly(mask, [pts], (255, 255, 255))

        # Apply Gaussian blur to the entire frame
        blurred_frame = cv2.GaussianBlur(frame, ksize, sigmaX)

        # Combine original and blurred image using the mask
        frame = np.where(mask == 255, blurred_frame, frame)
    else:
        x1, y1, x2, y2, conf = detection
        sub_face = frame[int(y1):int(y2), int(x1):int(x2)]
        sub_face = cv2.GaussianBlur(sub_face, ksize, sigmaX)
        frame[int(y1):int(y2), int(x1):int(x2)] = sub_face

def draw(self, classification_point, classes, frame):
    for class_index in classification_point.keys():
        try:
            for detection in classification_point[class_index]:
                name = classes[class_index]
                if name not in ('faces', 'face') and not context.context_dict[context.boxVisualization]:
                    continue

                if (int(class_index)) == 0:
                    self.draw_polygon(frame,detection,color=(0, 0, 255))

                elif name in ('faces', 'face') and context.context_dict[context.blurFace]:
                    self.blur_face(frame,detection)
                    if context.context_dict[context.boxVisualization]:
                        self.draw_polygon(frame,detection,color=(0, 255, 0))

                elif context.context_dict[context.boxVisualization]:
                    self.draw_polygon(frame,detection,color=(0, 255, 0),label=name)

        except Exception as e:
            pass

    return frame\n
` + '\n';
      this.stepname.forEach((step) => {
        Codedata +=
          `def detector_${this.getFunctionName(
            step
          )}(self, current_detection, classification_point):
  # write your code here!
  return False` + '\n';
      });
      Codedata +=
        `def get_predictions(self, current_detection, classification_point):
  # Get all methods in the class
  preds = []
  methods = ["detector_" + step.replace(" ", "_") for step in context.context_dict[context.currentTaskForInferenceSteps] if step.lower() != 'background']

  # Call all functions one by one
  for index,method in enumerate(methods):
    if method != "get_predictions":
      func = getattr(self, method, None)
      if func:
        result = func(current_detection, classification_point)
        if result is True:
          preds.append(index)

  return preds` + `\n`;
      this.customCode = Codedata;
      this.addClassInCustomCode(Codedata);
    },

    resetCode() {
      this.generateCompleteCode();
      this.extractCustomCodeLines(this.completeCode);
      this.parseCustomCodeTaskViseByFunction();
    },
  },
};
</script>

<style scoped>
.process-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
